import React, { useEffect, useState, useCallback } from 'react';
import axios, { AxiosError } from 'axios';
import { API_BASE_URL } from '../App';
import {
    Container, Box, Button, Paper, Typography,
    CircularProgress, Alert, Stack, Card, CardContent
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart'; // <-- ÍCONO AÑADIDO

// Interfaz para un punto de datos de series de tiempo
interface TimeSeriesDataPoint {
    timestamp: string;
    value: number;
}

// Interfaces para métricas de recursos del sistema
interface MemoryUsage {
    total: number; // en GB
    used: number;  // en GB
    percent: number; // en %
}

interface DiskUsage {
    total: number; // en GB
    used: number;  // en GB
    percent: number; // en %
}

interface SystemResources {
    cpu_load_percent: number;
    memory: MemoryUsage;
    disk: DiskUsage;
}

// Interfaz para la información de monitoreo que viene del backend (actualizada)
interface MonitoringData {
    timestamp: string;
    database: {
        status: string;
        total_clicks_in_db: number;
    };
    redis: {
        status: string;
        click_queue_length: number;
    };
    system_resources: SystemResources; // <--- NUEVA PROPIEDAD
    click_worker_status_note: string;
    overall_status: 'healthy' | 'degraded';
    redis_queue_history?: TimeSeriesDataPoint[];
    processed_clicks_rate_history?: TimeSeriesDataPoint[];
    cpu_load_history?: TimeSeriesDataPoint[];       // <--- NUEVA PROPIEDAD
    mem_usage_history?: TimeSeriesDataPoint[];      // <--- NUEVA PROPIEDAD
    disk_usage_history?: TimeSeriesDataPoint[];     // <--- NUEVA PROPIEDAD
}

const MonitoringPage: React.FC = () => {
    const [monitoringData, setMonitoringData] = useState<MonitoringData | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const theme = useTheme();

    const fetchMonitoringStatus = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await axios.get<MonitoringData>(`${API_BASE_URL}/monitor/status`);
            setMonitoringData(response.data);

        } catch (err: any) {
            if (axios.isAxiosError(err)) {
                const axiosError = err as AxiosError;

                if (axiosError.response) {
                    const errorDetails = axiosError.response.data;
                    let message = `Error del servidor: ${axiosError.response.status} ${axiosError.response.statusText}`;
                    if (typeof errorDetails === 'object' && errorDetails !== null && 'message' in errorDetails && typeof (errorDetails as any).message === 'string') {
                        message = (errorDetails as any).message;
                    } else if (typeof errorDetails === 'string') {
                        message = errorDetails;
                    }
                    setError(message);
                } else if (axiosError.request) {
                    setError('Error de red: No se recibió respuesta del servidor.');
                } else {
                    setError(`Error al configurar la petición: ${axiosError.message}`);
                }
            } else {
                setError(err.message || 'Error desconocido al cargar el estado de monitoreo.');
            }
            console.error("Error fetching monitoring status:", err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMonitoringStatus();
        const interval = setInterval(fetchMonitoringStatus, 15000); // Refresca cada 15 segundos
        return () => clearInterval(interval);
    }, [fetchMonitoringStatus]);

    // Función para formatear el timestamp para el eje X de los gráficos
    const formatTimestamp = (tickItem: string) => {
        const date = new Date(tickItem);
        // Formatear a HH:MM:SS para el eje X
        return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    };

    if (isLoading) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
                <Typography variant="h6" sx={{ ml: 2 }} color="text.secondary">Cargando estado del sistema...</Typography>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    <MonitorHeartIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '2.5rem' }} />
                    Monitoreo del Tracker
                </Typography>
                <Alert severity="error" sx={{ mt: 2 }}>
                    Error: {error}
                </Alert>
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Button variant="contained" color="primary" onClick={fetchMonitoringStatus}>
                        Reintentar Carga
                    </Button>
                </Box>
            </Container>
        );
    }

    if (!monitoringData) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    <MonitorHeartIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '2.5rem' }} />
                    Monitoreo del Tracker
                </Typography>
                <Alert severity="warning" sx={{ mt: 2 }}>
                    No se pudieron obtener los datos de monitoreo.
                </Alert>
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Button variant="contained" color="primary" onClick={fetchMonitoringStatus}>
                        Reintentar
                    </Button>
                </Box>
            </Container>
        );
    }

    const overallStatusColor = monitoringData.overall_status === 'healthy' ? theme.palette.success.main : theme.palette.warning.main;
    const dbStatusColor = monitoringData.database.status === 'connected' ? theme.palette.success.main : theme.palette.error.main;
    const redisStatusColor = monitoringData.redis.status === 'connected' ? theme.palette.success.main : theme.palette.error.main;

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                <MonitorHeartIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '2.5rem' }} />
                Monitoreo del Tracker
            </Typography>

            <Button
                variant="contained"
                color="primary"
                onClick={fetchMonitoringStatus}
                sx={{ mb: 4 }}
                disabled={isLoading}
            >
                Refrescar Estado
            </Button>

            <Stack spacing={4}>
                {/* Overall Status Card */}
                <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, borderRadius: '12px' }}>
                    <Typography variant="h5" component="h2" gutterBottom color="text.primary">
                        Estado General
                    </Typography>
                    <Typography variant="h6" sx={{ color: overallStatusColor, fontWeight: 'bold' }}>
                        {monitoringData.overall_status === 'healthy' ? 'Saludable ✅' : 'Degradado ⚠️'}
                    </Typography>
                </Paper>

                {/* Details Cards (DB & Redis) */}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={4}>
                    {/* Database Card */}
                    <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, borderRadius: '12px', flexGrow: 1 }}>
                        <Typography variant="h6" component="h3" gutterBottom color="text.primary">
                            Base de Datos (PostgreSQL)
                        </Typography>
                        <Typography variant="body1" sx={{ color: dbStatusColor, fontWeight: 'bold' }}>
                            Estado: {monitoringData.database.status}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Total Clics en DB: {monitoringData.database.total_clicks_in_db.toLocaleString()}
                        </Typography>
                    </Paper>

                    {/* Redis Card */}
                    <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, borderRadius: '12px', flexGrow: 1 }}>
                        <Typography variant="h6" component="h3" gutterBottom color="text.primary">
                            Cola de Clics (Redis)
                        </Typography>
                        <Typography variant="body1" sx={{ color: redisStatusColor, fontWeight: 'bold' }}>
                            Estado: {monitoringData.redis.status}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Clics en Cola: {monitoringData.redis.click_queue_length.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            {monitoringData.click_worker_status_note}
                        </Typography>
                    </Paper>
                </Stack>

                {/* --- SECCIÓN DE RECURSOS DEL SISTEMA ACTUALES --- */}
                <Typography variant="h5" component="h2" gutterBottom color="text.primary" sx={{ mt: 4 }}>
                    Recursos del Sistema (Actual)
                </Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={4}>
                    {/* CPU Card */}
                    <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, borderRadius: '12px', flexGrow: 1 }}>
                        <Typography variant="h6" component="h3" gutterBottom color="text.primary">
                            Uso de CPU
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {monitoringData.system_resources.cpu_load_percent}%
                        </Typography>
                    </Paper>

                    {/* Memory Card */}
                    <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, borderRadius: '12px', flexGrow: 1 }}>
                        <Typography variant="h6" component="h3" gutterBottom color="text.primary">
                            Uso de Memoria
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {monitoringData.system_resources.memory.percent}% (
                            {monitoringData.system_resources.memory.used} GB /{' '}
                            {monitoringData.system_resources.memory.total} GB
                            )
                        </Typography>
                    </Paper>

                    {/* Disk Card */}
                    <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, borderRadius: '12px', flexGrow: 1 }}>
                        <Typography variant="h6" component="h3" gutterBottom color="text.primary">
                            Uso de Disco
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {monitoringData.system_resources.disk.percent}% (
                            {monitoringData.system_resources.disk.used} GB /{' '}
                            {monitoringData.system_resources.disk.total} GB
                            )
                        </Typography>
                    </Paper>
                </Stack>


                {/* --- SECCIÓN DE GRÁFICOS DE TENDENCIAS --- */}
                <Typography variant="h5" component="h2" gutterBottom color="text.primary" sx={{ mt: 4 }}>
                    Tendencias de Métricas Clave
                </Typography>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} sx={{ mb: 2 }}>
                    {/* Gráfico: Longitud de la Cola de Redis */}
                    {monitoringData.redis_queue_history && monitoringData.redis_queue_history.length > 0 && (
                        <Card elevation={3} sx={{ p: 2, borderRadius: '12px', flexGrow: 1, minHeight: 300 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Longitud de la Cola de Redis
                                </Typography>
                                <ResponsiveContainer width="100%" height={200}>
                                    <LineChart data={monitoringData.redis_queue_history}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="timestamp"
                                            tickFormatter={formatTimestamp}
                                            angle={-45}
                                            textAnchor="end"
                                            height={60}
                                            interval="preserveStartEnd"
                                        />
                                        <YAxis />
                                        <Tooltip labelFormatter={formatTimestamp} />
                                        <Legend />
                                        <Line
                                            type="monotone"
                                            dataKey="value"
                                            stroke={theme.palette.primary.main}
                                            activeDot={{ r: 8 }}
                                            name="Elementos en Cola"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>
                    )}

                    {/* Gráfico: Tasa de Clics Procesados */}
                    {monitoringData.processed_clicks_rate_history && monitoringData.processed_clicks_rate_history.length > 0 && (
                        <Card elevation={3} sx={{ p: 2, borderRadius: '12px', flexGrow: 1, minHeight: 300 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Tasa de Clics Procesados (Clics/Intervalo)
                                </Typography>
                                <ResponsiveContainer width="100%" height={200}>
                                    <LineChart data={monitoringData.processed_clicks_rate_history}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="timestamp"
                                            tickFormatter={formatTimestamp}
                                            angle={-45}
                                            textAnchor="end"
                                            height={60}
                                            interval="preserveStartEnd"
                                        />
                                        <YAxis />
                                        <Tooltip labelFormatter={formatTimestamp} />
                                        <Legend />
                                        <Line
                                            type="monotone"
                                            dataKey="value"
                                            stroke={theme.palette.secondary.main}
                                            activeDot={{ r: 8 }}
                                            name="Tasa de Procesamiento"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>
                    )}
                </Stack>

                {/* --- Nuevos Gráficos de Recursos del Sistema --- */}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} sx={{ mb: 2 }}>
                    {/* Gráfico: Carga de CPU */}
                    {monitoringData.cpu_load_history && monitoringData.cpu_load_history.length > 0 && (
                        <Card elevation={3} sx={{ p: 2, borderRadius: '12px', flexGrow: 1, minHeight: 300 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Carga de CPU (%)
                                </Typography>
                                <ResponsiveContainer width="100%" height={200}>
                                    <LineChart data={monitoringData.cpu_load_history}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="timestamp"
                                            tickFormatter={formatTimestamp}
                                            angle={-45}
                                            textAnchor="end"
                                            height={60}
                                            interval="preserveStartEnd"
                                        />
                                        <YAxis domain={[0, 100]} /> {/* CPU siempre de 0 a 100 */}
                                        <Tooltip labelFormatter={formatTimestamp} />
                                        <Legend />
                                        <Line
                                            type="monotone"
                                            dataKey="value"
                                            stroke={theme.palette.info.main} // Un color diferente para CPU
                                            activeDot={{ r: 8 }}
                                            name="Carga CPU"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>
                    )}

                    {/* Gráfico: Uso de Memoria */}
                    {monitoringData.mem_usage_history && monitoringData.mem_usage_history.length > 0 && (
                        <Card elevation={3} sx={{ p: 2, borderRadius: '12px', flexGrow: 1, minHeight: 300 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Uso de Memoria (%)
                                </Typography>
                                <ResponsiveContainer width="100%" height={200}>
                                    <LineChart data={monitoringData.mem_usage_history}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="timestamp"
                                            tickFormatter={formatTimestamp}
                                            angle={-45}
                                            textAnchor="end"
                                            height={60}
                                            interval="preserveStartEnd"
                                        />
                                        <YAxis domain={[0, 100]} /> {/* Memoria siempre de 0 a 100 */}
                                        <Tooltip labelFormatter={formatTimestamp} />
                                        <Legend />
                                        <Line
                                            type="monotone"
                                            dataKey="value"
                                            stroke={theme.palette.warning.dark} // Un color diferente para Memoria
                                            activeDot={{ r: 8 }}
                                            name="Uso Memoria"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>
                    )}

                    {/* Gráfico: Uso de Disco */}
                    {monitoringData.disk_usage_history && monitoringData.disk_usage_history.length > 0 && (
                        <Card elevation={3} sx={{ p: 2, borderRadius: '12px', flexGrow: 1, minHeight: 300 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Uso de Disco (%)
                                </Typography>
                                <ResponsiveContainer width="100%" height={200}>
                                    <LineChart data={monitoringData.disk_usage_history}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="timestamp"
                                            tickFormatter={formatTimestamp}
                                            angle={-45}
                                            textAnchor="end"
                                            height={60}
                                            interval="preserveStartEnd"
                                        />
                                        <YAxis domain={[0, 100]} /> {/* Disco siempre de 0 a 100 */}
                                        <Tooltip labelFormatter={formatTimestamp} />
                                        <Legend />
                                        <Line
                                            type="monotone"
                                            dataKey="value"
                                            stroke={theme.palette.error.main} // Un color diferente para Disco
                                            activeDot={{ r: 8 }}
                                            name="Uso Disco"
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>
                    )}
                </Stack>
            </Stack>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 4, display: 'block', textAlign: 'right' }}>
                Última actualización: {new Date(monitoringData.timestamp).toLocaleString()}
            </Typography>
        </Container>
    );
};

export default MonitoringPage;